import { useQuery, gql, QueryHookOptions } from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLQueryMerchant_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs,
  GQLQueryMerchant_GetPlatformMerchantOrdersArgs,
  GQLPlatformMerchantOrderConnection,
  GQLPlatformSaleCatalogWithPlatformMerchantContractConnection,
  GQLPlatformSaleCatalogWithPlatformMerchantContract,
  GQLPlatformMerchantOrder,
  GQLQueryMerchant_GetPlatformMerchantOrderArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { PLATFORM_MERCHANT_ORDER_FIELDS } from './fragments/order';
import { PLATFORM_SALE_CATALOG_WITH_PLATFORM_MERCHANT_CONTRACT_FIELDS } from './fragments/platform';
import { OperationsHelper } from './OperationHelper';

export class MerchantOperation {
  platformMerchantOrderFromResponse(data: any): GQLPlatformMerchantOrder {
    if (data.Merchant_getPlatformMerchantOrder !== undefined) {
      return data.Merchant_getPlatformMerchantOrder;
    }
    throw new Exception(`Cannot handle input data platformMerchantOrderFromResponse`);
  }

  platformMerchantOrdersArrayFromResponse(data: any): GQLPlatformMerchantOrder[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'platformMerchantOrders');
  }

  platformSaleCatalogWithPlatformMerchantContractsArrayFromResponse(
    data: any
  ): GQLPlatformSaleCatalogWithPlatformMerchantContract[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(
      data,
      'platformSaleCatalogWithPlatformMerchantContracts'
    );
  }

  getPlatformMerchantOrder(
    args: GQLQueryMerchant_GetPlatformMerchantOrderArgs,
    options: QueryHookOptions<GQLPlatformMerchantOrder, GQLQueryMerchant_GetPlatformMerchantOrderArgs> = {}
  ) {
    const query = gql`
      ${PLATFORM_MERCHANT_ORDER_FIELDS}
      query getPlatformMerchantOrder($orderId: ID!) {
        Merchant_getPlatformMerchantOrder(orderId: $orderId) {
          ...PlatformMerchantOrderFields
        }
      }
    `;
    return useQuery<GQLPlatformMerchantOrder, GQLQueryMerchant_GetPlatformMerchantOrderArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPlatformMerchantOrders(args: GQLQueryMerchant_GetPlatformMerchantOrdersArgs) {
    const query = gql`
      ${PLATFORM_MERCHANT_ORDER_FIELDS}
      query getPlatformMerchantOrders($merchantPartyId: ID!, $merchantChannelId: ID!) {
        Merchant_getPlatformMerchantOrders(
          merchantPartyId: $merchantPartyId
          merchantChannelId: $merchantChannelId
        ) {
          cursor
          hasMore
          platformMerchantOrders {
            ...PlatformMerchantOrderFields
          }
        }
      }
    `;
    return useQuery<GQLPlatformMerchantOrderConnection, GQLQueryMerchant_GetPlatformMerchantOrdersArgs>(
      query,
      { variables: args }
    );
  }

  getPlatformSaleCatalogWithPlatformMerchantContracts(
    args: GQLQueryMerchant_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs
  ) {
    const query = gql`
      ${PLATFORM_SALE_CATALOG_WITH_PLATFORM_MERCHANT_CONTRACT_FIELDS}
      query getPlatformSaleCatalogWithPlatformMerchantContracts(
        $merchantPartyId: ID!
        $includeOnlyActiveContracts: Boolean!
      ) {
        Merchant_getPlatformSaleCatalogWithPlatformMerchantContracts(
          merchantPartyId: $merchantPartyId
          includeOnlyActiveContracts: $includeOnlyActiveContracts
        ) {
          cursor
          hasMore
          platformSaleCatalogWithPlatformMerchantContracts {
            ...PlatformSaleCatalogWithPlatformMerchantContractFields
          }
        }
      }
    `;
    return useQuery<
      GQLPlatformSaleCatalogWithPlatformMerchantContractConnection,
      GQLQueryMerchant_GetPlatformSaleCatalogWithPlatformMerchantContractsArgs
    >(query, { variables: args });
  }
}
